/**
 * Przelewy24 REST API adapter.
 *
 * Provides hooks for:
 * - Creating a P24 checkout session (redirect to paywall)
 * - Polling order status after return from P24
 */

import { useMutation, useQuery } from '@tanstack/react-query';

import { apiClient } from '@api/client';

// ----- Types -----

interface BillingAddressInput {
  email: string;
  first_name?: string;
  last_name?: string;
  address: string;
  city: string;
  postal_code: string;
  phone?: string;
  is_company: boolean;
  company_name?: string;
  tax_id?: string;
}

/** Plan from GET /p24/plans/ — netto + VAT z backendu, brutto liczone zawsze. */
export interface P24Plan {
  sku: string;
  name: string;
  description: string;
  net_amount: string;
  vat_rate: string;
  gross_amount: string;
  gross_grosze: number;
  currency: string;
}

/** Wejście: tylko offer_id + sku. Kwota z backendu (netto + vat_rate → brutto). */
export interface CreateCheckoutInput {
  billing_address: BillingAddressInput;
  offer_id: number;
  sku: string;
}

interface CreateCheckoutResponse {
  order_id: number;
  redirect_url: string;
  gross_amount?: string;
  currency?: string;
}

export type OrderStatus = 'pending' | 'paid' | 'failed' | 'not_found';

interface OrderStatusResponse {
  status: OrderStatus;
  order_id: number;
}

// ----- API calls -----

async function getP24Plans(): Promise<P24Plan[]> {
  const response = await apiClient.get<P24Plan[]>('/p24/plans/');
  return response.data;
}

async function createP24Checkout(input: CreateCheckoutInput): Promise<CreateCheckoutResponse> {
  const response = await apiClient.post<CreateCheckoutResponse>('/p24/create-checkout/', input);
  return response.data;
}

async function getOrderStatus(orderId: number): Promise<OrderStatusResponse> {
  const response = await apiClient.get<OrderStatusResponse>('/p24/order-status/', {
    params: { order_id: orderId },
  });
  return response.data;
}

// ----- Hooks -----

/**
 * Fetch P24 plans (Starter/Biznes/Premium) from backend. Single source of truth for price.
 */
export function useP24Plans() {
  return useQuery({
    queryKey: ['p24-plans'],
    queryFn: getP24Plans,
  });
}

/**
 * Hook for creating a P24 checkout session.
 * Returns a redirect URL to the P24 paywall.
 */
export function useCreateP24Checkout() {
  const mutation = useMutation({
    mutationFn: createP24Checkout,
  });

  return {
    createCheckout: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * Hook for polling order status after returning from P24.
 *
 * Polls every 3 seconds while status is "pending".
 * Stops polling when status becomes "paid" or "failed", or when stopPolling is true
 * (e.g. frontend decided to show "Płatność nieudana" after 45s pending).
 */
export function useOrderStatus(
  orderId: number | null,
  { enabled = true, stopPolling = false } = {}
) {
  return useQuery({
    queryKey: ['p24-order-status', orderId],
    queryFn: () => getOrderStatus(orderId!),
    enabled: enabled && orderId !== null,
    refetchInterval: (query) => {
      if (stopPolling) return false;
      const data = query.state.data;
      if (data?.status === 'pending') return 3000;
      return false; // stop polling when paid/failed
    },
  });
}
